import React, { useState } from "react";
import { Modal, DatePicker, Select, Input, Table, Alert, Tag } from "antd";
import moment from "moment";
import { useMutation } from "@apollo/client";

import { Button } from "../../../components/buttons/buttons";
import {
  ISSUE_COLLECTIVE_FROM_SERVICES,
  INVOICE_SERVICES_DIRECTLY,
} from "../../../graphql/InvoiceMutations";
import { stiahniZbernuFakturu, posliZbernuFakturu } from "../../../Api/zbernaFaktura";
import displayNotification from "../../reservation/admin/notification/notification";
import { formatPrice } from "../../../utils/common";

/**
 * FAKTURACIA PRIAMO V ZOZNAME ZAKAZIEK.
 *
 * Ziadna samostatna obrazovka. Zakazky sa vyfiltruju v zozname, zaskrtnu
 * a odtialto sa z nich bud vystavi zberna faktura (jedna na majitela),
 * alebo faktura po zakazke, teda to iste, co dnes robi tlacidlo na detaile,
 * len na viacerych naraz. Po vystaveni sa zakazke zmeni stav a do stlpca
 * "Cislo FA" pride cislo dokladu.
 */

const FAKTUROVATELNE = ["approved", "approved_manually"];

/**
 * Zakazka sa da vyfakturovat, len ked je schvalena a zatial nema doklad.
 * To iste overuje aj backend v jednej transakcii, toto je len preto, aby sa
 * neda zaskrtnut nieco, co by server aj tak odmietol.
 */
export const saDaFakturovat = (z) =>
  !z?.invoiceId &&
  !z?.invoice_number &&
  FAKTUROVATELNE.includes(String(z?.status));

const suma = (zakazky) =>
  zakazky.reduce((spolu, z) => spolu + Number(z?.total || 0), 0);

/** Vybrane zakazky zoskupene po majiteloch, bo zberna faktura je vzdy za jedneho. */
const podlaMajitela = (zakazky) => {
  const mapa = new Map();
  zakazky.forEach((z) => {
    const kluc = Number(z?.carOwner?.id || 0);
    if (!mapa.has(kluc)) {
      mapa.set(kluc, {
        carOwnerId: kluc,
        company: z?.carOwner?.company || "bez majiteľa",
        zakazky: [],
      });
    }
    mapa.get(kluc).zakazky.push(z);
  });
  return Array.from(mapa.values()).sort((a, b) =>
    String(a.company).localeCompare(String(b.company), "sk")
  );
};

const Fakturacia = (props) => {
  const { vybrane, onVycistit, onHotovo } = props;

  const [rezim, setRezim] = useState(null);
  const [datum, setDatum] = useState(moment());
  const [sposob, setSposob] = useState("bank");
  const [poznamka, setPoznamka] = useState("");
  const [vysledok, setVysledok] = useState(null);
  const [posielam, setPosielam] = useState(null);

  const [vystavZbernu, { loading: bezimZbernu }] = useMutation(
    ISSUE_COLLECTIVE_FROM_SERVICES
  );
  const [vystavRovno, { loading: bezimRovno }] = useMutation(
    INVOICE_SERVICES_DIRECTLY
  );
  const bezim = bezimZbernu || bezimRovno;

  const fakturovatelne = (vybrane || []).filter(saDaFakturovat);
  const skupiny = podlaMajitela(fakturovatelne);

  if (!vybrane || vybrane.length === 0) return null;

  const zavriPotvrdenie = () => {
    if (bezim) return;
    setRezim(null);
  };

  const spracuj = (odpoved, typ) => {
    if (!odpoved) {
      displayNotification("error", "Fakturácia", "Server neodpovedal");
      return;
    }
    // `sucessful` chodi z GraphQL ako retazec "true" / "false", nie ako bool.
    if (String(odpoved.sucessful) === "false") {
      displayNotification(
        "error",
        "Fakturácia",
        odpoved.message || "Fakturácia zlyhala"
      );
      return;
    }
    setRezim(null);
    setVysledok({ ...odpoved, typ });
  };

  const potvrd = async () => {
    const serviceIds = fakturovatelne.map((z) => Number(z.id));
    const spolocne = {
      tokenMutation: process.env.REACT_APP_TOKEN_MUTATION,
      serviceIds,
      issueDate: (datum || moment()).format("YYYY-MM-DD"),
      paymentType: sposob,
    };
    try {
      if (rezim === "zberna") {
        const { data } = await vystavZbernu({
          variables: { ...spolocne, note: poznamka || undefined },
        });
        spracuj(data?.issueCollectiveFromServices, "zberna");
      } else {
        const { data } = await vystavRovno({ variables: spolocne });
        spracuj(data?.invoiceServicesDirectly, "rovno");
      }
    } catch (chyba) {
      displayNotification(
        "error",
        "Fakturácia",
        chyba?.message || "Fakturácia zlyhala"
      );
    }
  };

  const zavriVysledok = () => {
    setVysledok(null);
    setPoznamka("");
    if (typeof onHotovo === "function") onHotovo();
  };

  const stiahni = async (faktura) => {
    setPosielam(`stiahnut-${faktura.invoiceId}`);
    const v = await stiahniZbernuFakturu(faktura.invoiceId, faktura.number);
    setPosielam(null);
    if (!v.status) {
      displayNotification("error", "Faktúra", "PDF sa nepodarilo vytvoriť");
    }
  };

  const posli = async (faktura) => {
    const ok = window.confirm(
      `Poslať faktúru ${faktura.number} zákazníkovi, s kópiou účtovníčke?`
    );
    if (!ok) return;
    setPosielam(`poslat-${faktura.invoiceId}`);
    const v = await posliZbernuFakturu(faktura.invoiceId);
    setPosielam(null);
    if (v.status && String(v.data?.sucessful) !== "false") {
      displayNotification("success", "Faktúra", `Faktúra ${faktura.number} odoslaná`);
    } else {
      const sprava =
        v?.error?.response?.data?.message ||
        v?.data?.message ||
        "Faktúra nebola odoslaná";
      displayNotification("error", "Faktúra", sprava);
    }
  };

  const neschvalene = (vybrane || []).length - fakturovatelne.length;

  return (
    <>
      {/* Lista nad tabulkou. Vidno ju, len ked je nieco zaskrtnute. */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "10px",
          margin: "0 0 16px 0",
          padding: "12px 16px",
          background: "#f4f5f7",
          border: "1px solid #e3e6ef",
          borderRadius: "6px",
        }}
      >
        <strong>
          Vybraté: {vybrane.length}{" "}
          {vybrane.length === 1 ? "zákazka" : vybrane.length < 5 ? "zákazky" : "zákaziek"}
        </strong>
        <span>Spolu {formatPrice(suma(fakturovatelne))}</span>
        {skupiny.length > 0 && (
          <span>
            {skupiny.length === 1
              ? `Majiteľ: ${skupiny[0].company}`
              : `Majiteľov: ${skupiny.length}`}
          </span>
        )}
        {neschvalene > 0 && (
          <span style={{ color: "#c0392b" }}>
            {neschvalene} sa fakturovať nedá, fakturujú sa len schválené zákazky
          </span>
        )}
        <span style={{ flex: 1 }} />
        <Button
          type="primary"
          size="small"
          disabled={fakturovatelne.length === 0}
          onClick={() => setRezim("zberna")}
        >
          Zbernou faktúrou
        </Button>
        <Button
          type="success"
          size="small"
          disabled={fakturovatelne.length === 0}
          onClick={() => setRezim("rovno")}
        >
          Fakturovať rovno
        </Button>
        <Button type="light" size="small" onClick={onVycistit}>
          Zrušiť výber
        </Button>
      </div>

      <Modal
        title={
          rezim === "zberna" ? "Vystaviť zbernú faktúru" : "Fakturovať po zákazke"
        }
        visible={rezim !== null}
        onCancel={zavriPotvrdenie}
        maskClosable={!bezim}
        footer={[
          <Button key="zrus" type="light" onClick={zavriPotvrdenie} disabled={bezim}>
            Zrušiť
          </Button>,
          <Button key="ok" type="primary" onClick={potvrd} disabled={bezim}>
            {bezim ? "Vystavujem..." : "Vystaviť"}
          </Button>,
        ]}
      >
        {rezim === "zberna" ? (
          <>
            <p>
              Vystaví sa {skupiny.length}{" "}
              {skupiny.length === 1 ? "faktúra" : skupiny.length < 5 ? "faktúry" : "faktúr"},
              jedna na majiteľa, z {fakturovatelne.length} zákaziek.
            </p>
            <Table
              size="small"
              pagination={false}
              rowKey={(r) => r.carOwnerId}
              dataSource={skupiny}
              columns={[
                { title: "Majiteľ", dataIndex: "company", key: "company" },
                {
                  title: "Zákaziek",
                  key: "pocet",
                  render: (r) => r.zakazky.length,
                },
                {
                  title: "Spolu",
                  key: "suma",
                  render: (r) => formatPrice(suma(r.zakazky)),
                },
              ]}
            />
          </>
        ) : (
          <p>
            Každá zo {fakturovatelne.length} zákaziek dostane vlastnú faktúru
            a vlastné číslo, presne ako pri fakturovaní z detailu zákazky.
          </p>
        )}

        <div style={{ marginTop: "16px" }}>
          <label style={{ display: "block", marginBottom: "4px" }}>
            Dátum vystavenia
          </label>
          <DatePicker
            value={datum}
            format="DD.MM.YYYY"
            allowClear={false}
            onChange={(d) => setDatum(d || moment())}
            style={{ width: "100%" }}
          />
        </div>
        <div style={{ marginTop: "12px" }}>
          <label style={{ display: "block", marginBottom: "4px" }}>Spôsob platby</label>
          <Select value={sposob} onChange={setSposob} style={{ width: "100%" }}>
            <Select.Option value="bank">Banka</Select.Option>
            <Select.Option value="cash">Hotovosť</Select.Option>
          </Select>
        </div>
        {rezim === "zberna" && (
          <div style={{ marginTop: "12px" }}>
            <label style={{ display: "block", marginBottom: "4px" }}>
              Poznámka na doklade (nepovinná)
            </label>
            <Input
              value={poznamka}
              onChange={(e) => setPoznamka(e.target.value)}
              placeholder="napríklad Fakturácia za október 2026"
            />
          </div>
        )}
        <Alert
          style={{ marginTop: "16px" }}
          type="info"
          showIcon
          message="Číslo ide z toho istého číselného radu ako doteraz. Po vystavení sa zákazka už upraviť nedá, opraviť sa dá len dobropisom."
        />
      </Modal>

      <Modal
        title="Vystavené faktúry"
        visible={vysledok !== null}
        onCancel={zavriVysledok}
        width={760}
        footer={[
          <Button key="ok" type="primary" onClick={zavriVysledok}>
            Zavrieť
          </Button>,
        ]}
      >
        {vysledok && vysledok.faktury && vysledok.faktury.length > 0 && (
          <>
            {vysledok.typ === "zberna" ? (
              <Table
                size="small"
                pagination={false}
                rowKey={(r) => r.invoiceId}
                dataSource={vysledok.faktury}
                columns={[
                  {
                    title: "Číslo",
                    dataIndex: "number",
                    key: "number",
                    render: (c) => <strong>{c}</strong>,
                  },
                  { title: "Majiteľ", dataIndex: "company", key: "company" },
                  { title: "Zákaziek", dataIndex: "pocetZakaziek", key: "pocetZakaziek" },
                  {
                    title: "Spolu s DPH",
                    dataIndex: "brutto",
                    key: "brutto",
                    render: (s) => formatPrice(Number(s || 0)),
                  },
                  {
                    title: "",
                    key: "akcie",
                    render: (r) => (
                      <div style={{ display: "flex", gap: "6px" }}>
                        <Button
                          size="small"
                          type="light"
                          disabled={posielam === `stiahnut-${r.invoiceId}`}
                          onClick={() => stiahni(r)}
                        >
                          PDF
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          disabled={posielam === `poslat-${r.invoiceId}`}
                          onClick={() => posli(r)}
                        >
                          Poslať
                        </Button>
                      </div>
                    ),
                  },
                ]}
              />
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey={(r) => r.serviceId}
                dataSource={vysledok.faktury}
                columns={[
                  { title: "Zákazka", dataIndex: "serviceId", key: "serviceId" },
                  {
                    title: "Číslo faktúry",
                    dataIndex: "number",
                    key: "number",
                    render: (c) => <strong>{c}</strong>,
                  },
                ]}
              />
            )}
          </>
        )}
        {vysledok && (!vysledok.faktury || vysledok.faktury.length === 0) && (
          <Alert
            type="warning"
            showIcon
            message="Nevystavila sa ani jedna faktúra."
          />
        )}
        {vysledok && vysledok.preskocene && vysledok.preskocene.length > 0 && (
          <div style={{ marginTop: "16px" }}>
            <p style={{ marginBottom: "6px" }}>
              <Tag color="orange">Preskočené</Tag> tieto zákazky sa nevyfakturovali:
            </p>
            <Table
              size="small"
              pagination={false}
              rowKey={(r) => r.serviceId}
              dataSource={vysledok.preskocene}
              columns={[
                { title: "Zákazka", dataIndex: "serviceId", key: "serviceId" },
                { title: "Dôvod", dataIndex: "dovod", key: "dovod" },
              ]}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default Fakturacia;
